import { getNotRequiredFields } from '../../utils/helper-functions/array-comparison';

const applyTemplate = (template) => {
  if (template) return template;
  return {};
};

const defaultDigitalWalletsData = {
  paymentInstrument: 'card', // EPaymentInstrument.Card
  // PRX-23109 (Revision 2, N1): the old, in-page "Card or wallet" toggle's
  // own current choice -- kept separate from `paymentInstrument` above,
  // which useDigitalWallets.ts syncs to whichever gateway the customer
  // selected from the payment-method list (see EWalletConfigMode).
  embeddedToggleInstrument: 'card', // EPaymentInstrument.Card
  // PRX-23109 (T4.31, user-reported): "has a default embedded-wallet
  // selection already been decided (applied, or found unnecessary) for the
  // CURRENT gateway selection" -- used to live in a component-local
  // `useRef` inside useDigitalWallets.ts (`hasAppliedEmbeddedDefaultRef`),
  // which meant every separate component calling that hook got its own,
  // independent answer. A component mounting later than another one (e.g.
  // the panoramic billing page's own SubmitButton, mounted only once the
  // customer reaches that page) started with a fresh `false` regardless of
  // what an earlier-mounted component had already decided, so it silently
  // re-ran the default-selection effect and overwrote an already-made,
  // explicit "Card" choice with a ready wallet. Moved here so every
  // useDigitalWallets() call site reads and writes the exact same answer.
  // Reset alongside `embeddedToggleInstrument` itself, only on a genuine
  // gateway/method switch (see getDigitalWalletDataByOptions below) -- an
  // unrelated re-render of the same selection must not re-arm this.
  hasAppliedEmbeddedWalletDefault: false,
  // PRX-23109 (T4.35, user-reported, regression from T4.33): mirrors
  // credit-card-form.js's own local `isAddCardMode` (`!isTemplate`) --
  // useDigitalWallets.ts cannot read that component's local React state
  // directly, and isSavedCardTemplateActiveForEmbeddedWallet
  // (wallet-visibility.ts) needs to tell "this gateway has a recently-used
  // template available" apart from "the customer is actually, currently
  // choosing to use it right now", since switching to "add a new card" for
  // the SAME underlying gateway does not necessarily clear
  // currentGateway.recently_used_template itself. Same
  // local-state-needs-to-reach-another-hook precedent as
  // hasAppliedEmbeddedWalletDefault/isDeviceCompatible above. Reset
  // alongside hasAppliedEmbeddedWalletDefault itself, only on a genuine
  // gateway/method switch (see getDigitalWalletDataByOptions below) --
  // credit-card-form.js's own effects re-derive and re-dispatch the real
  // value again immediately after any such switch anyway.
  isAddCardModeSelected: false,
  digitalWalletError: null,
  digitalWalletTokenizedData: null,
  isWalletWindowOpen: false,
  digitalBillingAddress: null,
  cardTokenizationOptions: null,
  isAmountShaking: false,
  isFirstAmountShaking: true,
  picInitiated: false,
  gPayData: {
    isReadyToPay: false,
    isGPayEnabledOnApp: false,
    removeBtnFromDOM: false,
    isTokenizationChecked: !window?.CASHIER_CONFIG,
    // PRX-23109 (T4.30): tri-state result of the 2.4.3 device-compatibility
    // pre-check (checkDigitalWalletDeviceCompatibility.ts's
    // filterUnsupportedDigitalWalletGateways, dispatched from fetchGateways).
    // `null` means that pre-check never ran for this wallet this session
    // (no separate google_pay_cc tile, or a withdrawal) and must stay
    // maximally permissive, exactly like before this signal existed;
    // `true`/`false` is its own confirmed result. Unlike isReadyToPay/
    // isTokenizationChecked/removeBtnFromDOM just above (which describe the
    // *currently selected* gateway's own live SDK check, correctly reset on
    // a gateway switch below), this describes the physical device itself
    // for the rest of the page session -- see getDigitalWalletDataByOptions
    // below for how it survives every such reset instead.
    isDeviceCompatible: null,
  },
  applePayData: {
    isReadyToPay: false,
    isApplePayEnabledOnApp: false,
    removeBtnFromDOM: false,
    isTokenizationChecked: !window?.CASHIER_CONFIG,
    isDeviceCompatible: null,
  },
};

const findTokenizationOption = (cardTokenizationOptions, provider) =>
  Array.isArray(cardTokenizationOptions)
    ? cardTokenizationOptions.find((option) => option?.token_provider === provider)
    : null;

const getDigitalWalletDataByOptions = (state, action) => {
  const cardTokenizationOptions = action.payload?.card_tokenization_options;
  const isCC = action.payload?.payment_method_type === 'cc';

  const googlePayOption = findTokenizationOption(cardTokenizationOptions, 'google_pay');
  const applePayOption = findTokenizationOption(cardTokenizationOptions, 'apple_pay');

  // PRX-23109 (T4.30): isDeviceCompatible is a property of the physical
  // device for this page session, not of whichever gateway happens to be
  // selected right now -- carry forward whatever this session already
  // knows about it through every branch below (a still-offered provider,
  // a dropped provider, and even a switch away from Credit Card entirely),
  // instead of letting any of those resets below wipe it back to `null`
  // (unknown).
  const gPayDeviceCompatible = state.payInfo.digitalWallets.gPayData?.isDeviceCompatible ?? null;
  const applePayDeviceCompatible =
    state.payInfo.digitalWallets.applePayData?.isDeviceCompatible ?? null;

  // PRX-23109 (T4.31, user-reported): mirrors the reasoning already
  // established for `embeddedToggleInstrument` not being reset by an
  // unrelated re-render of the SAME selection (see the doc comment on
  // `hasAppliedEmbeddedWalletDefault` in `defaultDigitalWalletsData` above)
  // -- `state` here is the whole PREVIOUS store tree (this custom root
  // reducer, reducers/index.js, hands every slice reducer the same
  // pre-action state), so `state.currentGateway?.payment_method_key` is the
  // OLD selection and `action.payload?.payment_method_key` is the NEW one.
  // Comparing them tells a genuine gateway/method switch apart from a
  // 'CREATE_CURRENT_GATEWAY' dispatched again for the same selection.
  const isGenuineMethodSwitch =
    state.currentGateway?.payment_method_key !== action.payload?.payment_method_key;

  // PRX-23109 (user-reported): gPayData/applePayData's own isReadyToPay/
  // isTokenizationChecked/removeBtnFromDOM used to be spread forward
  // unconditionally from whatever gateway was selected before -- on
  // purpose, so a check already in flight for the *same* gateway/provider
  // survives an unrelated re-render. But that also means a provider the
  // newly selected gateway doesn't even offer (isXEnabledOnApp now false)
  // keeps whatever readiness a DIFFERENT, previously selected gateway last
  // reported for it -- observed live: selecting a Credit Card gateway that
  // only lists google_pay left applePayData.isReadyToPay/
  // isTokenizationChecked stuck `true` from an earlier apple_pay_cc
  // selection. Harmless for isEmbeddedWalletReady itself here (already
  // ANDed with isApplePayEnabledOnApp, correctly false), but it would
  // permanently block a *future*, genuine onTokenizationAvailability event
  // for apple_pay on a later gateway that does offer it again, since
  // onTokenizationAvailabilityEvent skips a provider whose
  // isTokenizationChecked is already true. Reset a provider's own check
  // state back to its default whenever the newly selected gateway doesn't
  // offer it at all; a provider that's still offered keeps its in-flight
  // state exactly as before.
  return isCC
    ? {
        ...state.payInfo.digitalWallets,
        embeddedToggleInstrument: isGenuineMethodSwitch
          ? defaultDigitalWalletsData.embeddedToggleInstrument
          : state.payInfo.digitalWallets.embeddedToggleInstrument,
        // PRX-23109 (T4.31): only reset on a genuine switch -- carried forward
        // otherwise, exactly like embeddedToggleInstrument itself just above.
        hasAppliedEmbeddedWalletDefault: isGenuineMethodSwitch
          ? false
          : state.payInfo.digitalWallets.hasAppliedEmbeddedWalletDefault,
        // PRX-23109 (T4.35): mirrors hasAppliedEmbeddedWalletDefault's own
        // reset rule just above -- see this field's own doc comment in
        // defaultDigitalWalletsData.
        isAddCardModeSelected: isGenuineMethodSwitch
          ? false
          : state.payInfo.digitalWallets.isAddCardModeSelected,
        cardTokenizationOptions: {
          google_pay: googlePayOption || null,
          apple_pay: applePayOption || null,
        },
        gPayData: {
          ...(googlePayOption
            ? state.payInfo.digitalWallets.gPayData
            : defaultDigitalWalletsData.gPayData),
          isGPayEnabledOnApp: Boolean(googlePayOption?.enabled),
          isDeviceCompatible: gPayDeviceCompatible,
        },
        applePayData: {
          ...(applePayOption
            ? state.payInfo.digitalWallets.applePayData
            : defaultDigitalWalletsData.applePayData),
          isApplePayEnabledOnApp: Boolean(applePayOption?.enabled),
          isDeviceCompatible: applePayDeviceCompatible,
        },
      }
    : {
        ...defaultDigitalWalletsData,
        gPayData: {
          ...defaultDigitalWalletsData.gPayData,
          isDeviceCompatible: gPayDeviceCompatible,
        },
        applePayData: {
          ...defaultDigitalWalletsData.applePayData,
          isDeviceCompatible: applePayDeviceCompatible,
        },
      };
};

const changePayInfo = (state, action) => {
  if (state === undefined) {
    return {
      initMethod: false,
      digitalWallets: defaultDigitalWalletsData,
      template: {},
      templateKey: {},
      gatewaySelectionSource: 'unknown',
      isGatewayDetailsLoading: false,
      checkbox: true,
      required: [],
      waste: [],
      visibleFields: null,
      changedPayData: null,
      manualRedirect: false,
      inputEditorCachedValue: {},
      errorsInfo: {},
      showBdccModal: false,
      showCardValidationModal: false,
      pciPraxisGate: {},
      customerCards: null,
      showGatewayPickerModal: false,
      isNewEditableTemplate: false, // use for additional fields (with key is_editable)
      allMethodsInMaintenance: false,
    };
  }

  switch (action.type) {
    // A tab switch throws away everything the player entered or chose on the
    // previous tab. The PCI gate and the customer's stored cards belong to the
    // session, not to the tab, so they are left alone.
    case 'RESET_TAB_STATE':
      return {
        ...state.payInfo,
        initMethod: false,
        templateKey: {},
        template: {},
        checkbox: true,
        visibleFields: null,
        required: [],
        waste: [],
        changedPayData: null,
        manualRedirect: false,
        errorsInfo: {},
        inputEditorCachedValue: {},
        gatewaySelectionSource: 'unknown',
        showGatewayPickerModal: false,
        allMethodsInMaintenance: false,
        isNewEditableTemplate: false,
        digitalWallets: defaultDigitalWalletsData,
      };
    case 'CREATE_CURRENT_GATEWAY':
      return {
        ...state.payInfo,
        initMethod: false,
        templateKey: {},
        template: {},
        checkbox: true,
        visibleFields: null,
        required: [],
        waste: [],
        changedPayData: null,
        manualRedirect: false,
        errorsInfo: {},
        digitalWallets: getDigitalWalletDataByOptions(state, action),
      };
    case 'CREATE_CURRENT_GATEWAY_FROM_AMOUNT_PAGE':
      return {
        ...state.payInfo,
        initMethod: false,
        templateKey: {},
        template: {},
        checkbox: true,
        visibleFields: null,
        required: [],
        waste: [],
        changedPayData: null,
        manualRedirect: false,
        errorsInfo: {},
      };
    case 'SET_GATEWAY_SELECTION_SOURCE':
      return {
        ...state.payInfo,
        gatewaySelectionSource: action.payload,
      };
    case 'SET_GATEWAY_DETAILS_LOADING':
      return {
        ...state.payInfo,
        isGatewayDetailsLoading: action.payload,
      };
    case 'APPLY_TEMPLATE':
      const { value, key } = action.payload;
      return {
        ...state.payInfo,
        template: applyTemplate(value),
        templateKey: key,
        isNewEditableTemplate: !key && !value,
        inputEditorCachedValue: {},
      };
    case 'SET_CHECKBOX':
      return {
        ...state.payInfo,
        checkbox: !state.payInfo.checkbox,
      };
    case 'SET_REQUIRED':
      let notRequiredFields = getNotRequiredFields(
        state.payInfo.notRequiredFields,
        state.payInfo.required,
        action.payload,
      );

      return {
        ...state.payInfo,
        required: action.payload,
        notRequiredFields,
      };
    case 'SET_WASTE':
      return {
        ...state.payInfo,
        waste: action.payload,
      };
    case 'CHANGE-PAY-DATA':
      return {
        ...state.payInfo,
        changedPayData: action.payload,
      };
    case 'UPDATE-PAY-DATA':
      return {
        ...state.payInfo,
        changedPayData: {
          ...state.payInfo.changedPayData,
          [action.payload.name]: action.payload.value,
        },
      };
    case 'SET_ADD_FIELDS_INFO':
      return {
        ...state.payInfo,
        ...action.payload,
      };
    case 'INIT_METHOD_STATE':
      return {
        ...state.payInfo,
        waste: action.payload.waste,
        required: action.payload.required,
        type: action.payload.type,
        initMethod: true,
        inputEditorCachedValue: {},
        showBdccModal: false,
        showCardValidationModal: false,
        pciPraxisGate: {},
      };
    case 'SET_INPUT_EDITOR_CACHE_VALUE':
      return {
        ...state.payInfo,
        inputEditorCachedValue: {
          ...state.payInfo.inputEditorCachedValue,
          ...action.payload,
        },
      };
    case 'SET_BDCC_MODAL':
      return {
        ...state.payInfo,
        showBdccModal: !state.payInfo.showBdccModal,
      };
    case 'SET_CARD_VALIDATION_MODAL':
      return {
        ...state.payInfo,
        showCardValidationModal: !state.payInfo.showCardValidationModal,
      };
    case 'SET_PCI_PRAXIS_GATE':
      return {
        ...state.payInfo,
        pciPraxisGate: { ...action.payload },
      };
    case 'SET_CUSTOMER_CARDS':
      return {
        ...state.payInfo,
        customerCards: action.payload,
      };
    case 'SET_GATEWAY_PICKER_MODAL':
      return {
        ...state.payInfo,
        showGatewayPickerModal: action.payload,
      };
    case 'UPDATE_DIGITAL_WALLET':
      return {
        ...state.payInfo,
        digitalWallets: {
          ...state.payInfo.digitalWallets,
          ...action.payload,
        },
      };
    case 'UPDATE_G_PAY_INFO':
      return {
        ...state.payInfo,
        digitalWallets: {
          ...state.payInfo.digitalWallets,
          gPayData: {
            ...state.payInfo.digitalWallets.gPayData,
            ...action.payload,
          },
        },
      };
    case 'UPDATE_APPLE_PAY_INFO':
      return {
        ...state.payInfo,
        digitalWallets: {
          ...state.payInfo.digitalWallets,
          applePayData: {
            ...state.payInfo.digitalWallets.applePayData,
            ...action.payload,
          },
        },
      };
    default:
      return state.payInfo;
  }
};

export { changePayInfo };
